<template>
  <div class="payment-detail-content">
    <div class="content-block">
      <p class="content-block__label">{{ $t('billing_transaction_time') }}</p>

      <div class="content-block__context">
        <div class="block-text">{{ txnTimeText }}</div>
      </div>
    </div>

    <InvoiceSection v-if="invoiceSection" :invoice-number="invoiceNumber" :invoice-carrier-row="invoiceCarrierRow" :padding-between-rows="true" />

    <div class="content-block pb-0">
      <p class="content-block__label">{{ $t('billing_content') }}</p>

      <div class="content-block__context">
        <div v-if="txnPaymentMethodText" class="block-text">
          <span> {{ $t('order_detail_payment_method_title') }} </span>
          <span> {{ txnPaymentMethodText }} </span>
        </div>

        <div class="block-text pb-4">
          <span> {{ $t('billing_order_amount') }} </span>
          <span> {{ totalAmount }} </span>
        </div>

        <template v-if="isProductHasSubitem && isOrderGot">
          <div :key="order.product.id" class="d-flex flex-column">
            <div class="block-text py-4">
              <span>{{ `${order.product.qty} x ${order.product.name}` }}</span>
              <span style="min-width: fit-content"> {{ `${order.symbol_code} ${order.product.total_price}` }} </span>
            </div>
            <div v-for="payload in productAmountLabels" :key="payload.titleLabel" class="block-text py-4 pl-4">
              <span>{{ payload.titleLabel }}</span>
              <span style="min-width: fit-content">{{ payload.priceLabel }}</span>
            </div>
          </div>

          <template v-if="order.additions.length > 0">
            <div v-for="addition in order.additions" :key="addition.id" class="block-text py-4">
              <span>{{ addition.qty }} x {{ addition.name }}</span>
              <span>{{ order.symbol_code }} {{ addition.total_price }}</span>
            </div>
          </template>
        </template>

        <div v-if="promoLabelText.length" class="block-text mb-3 d-flex">
          <span> {{ $t('billing_promo') }} </span>

          <div class="block-text--right" style="max-width: 192px">
            <p class="mb-1">{{ promoTotalAmountText }}</p>
            <p class="sub-title">{{ promoLabelText }}</p>
          </div>
        </div>

        <div v-if="bonusDiscountText" class="block-text mb-3">
          <div class="d-flex align-center">
            <span> {{ $t('billing_fun_coin', { funcoin: order.bonus }) }} </span>
            <funnow-icon class="ml-1" max-width="16" image-path="fun_coins.svg" />
          </div>
          <span> {{ bonusDiscountText }} </span>
        </div>
      </div>
    </div>

    <div class="content-block">
      <div class="content-block__context">
        <div class="block-text">
          <span> {{ $t('billing_paid_amount') }} </span>
          <span class="wd-text-orange50"> {{ txnAmount }} </span>
        </div>
      </div>
    </div>
    <div class="wd-px-5 sm:wd-px-0 wd-pt-2 wd-text-gray80 wd-text-sm" :class="[!fromTngMiniProgram && showCommentInfo ? 'wd-pb-2' : 'wd-pb-4']">
      {{ $t('order_detail_3rd_party_payment_notice') }}
    </div>

    <div v-if="!fromTngMiniProgram && showCommentInfo" class="content-block">
      <div class="content-block__context">
        <div class="block-text">
          <FunCoinRewardBlock :fun-coin-source="order" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmpty as _isEmpty } from 'lodash-es'

import FunnowIcon from '@/components/icons/funnow-icon.vue'
import FunCoinRewardBlock from '@/components/FunCoinRewardBlock.vue'
import InvoiceSection from '@/components/invoice/InvoiceSection.vue'
import { useInvoiceDisplay } from '@/composition/useInvoiceDisplay'
import { useOrderFlags } from '@/composition/useOrder'
import type { OrderDetail } from '@/view-models/order'

type Props = {
  order: OrderDetail
  showCommentInfo?: boolean
}

defineOptions({
  name: 'PaymentDetailSheet',
})

const props = withDefaults(defineProps<Props>(), {
  order: () => ({} as OrderDetail),
  showCommentInfo: true,
})

const { order } = toRefs(props)
const { invoiceNumber, invoiceSection, invoiceCarrierRow } = useInvoiceDisplay(computed(() => props.order.invoice_info))
const nuxtApp = useNuxtApp()
const { $store: store } = useNuxtApp()
const { isPaymentPaid, isProductHasSubitem } = useOrderFlags(order)

const fromTngMiniProgram = computed(() => store.getters.fromTngMiniProgram)

const isOrderGot = computed(() => !_isEmpty(props.order))

const txnPaymentMethodText = computed(() => {
  return props.order?.txn_info?.payment_method_type_text || ''
})
const txnTimeText = computed(() => {
  if (!isPaymentPaid.value) return nuxtApp.$i18n.t('order_detail_payment_not_finished')
  return props.order.txn_info ? nuxtApp.$dayjs(props.order.txn_info.txn_time, { utc: true }).local().format('YYYY/MM/DD HH:mm') : ''
})

const totalAmount = computed(() => {
  return props.order.total_amount ? `${props.order.symbol_code} ${props.order.total_amount?.toLocaleString()}` : ''
})

const promoDiscount = computed(() => {
  if (props.order.promo && props.order.promo.discount) {
    const promoDiscount = props.order.promo.discount * props.order.promo.usage_quantity
    return `- ${props.order.symbol_code} ${promoDiscount?.toLocaleString()}`
  }

  return ''
})

const promoName = computed(() => {
  return props.order.promo ? props.order.promo.name : ''
})

// from props `order.promos`
const promoLabelText = computed(() => {
  return props.order.promos?.map(promo => (promo.usage_quantity > 1 ? `${promo.usage_quantity}x ${promo.name}` : promo.name)).join(' + ') || ''
})

const promoTotalAmountText = computed(() => {
  const totalAmount = props.order.promos?.reduce((totalAmount, promo) => totalAmount + (promo.usage_quantity || 0) * (promo.discount || 0), 0)

  return `- ${props.order.symbol_code} ${totalAmount?.toLocaleString()}`
})

const txnAmount = computed(() => {
  if (!isPaymentPaid.value) return nuxtApp.$i18n.t('payment_detail_no_txn_amount')
  return props.order.txn_info ? `${props.order.txn_info.symbol_code} ${props.order.txn_info.amount?.toLocaleString()}` : ''
})

const bonusDiscountText = computed(() => {
  return props.order.bonus_discount ? `- ${props.order.symbol_code} ${props.order.bonus_discount?.toLocaleString()}` : ''
})

const productAmountLabels = computed(() => {
  return (
    props.order.product?.sub_item_prices?.map((productAmount, index) => ({
      titleLabel: `1 x ${props.order.product.name} ${index + 1}`,
      priceLabel: `${props.order.symbol_code} ${productAmount}`,
    })) || []
  )
})
</script>

<style lang="scss" scoped>
.payment-detail-content {
  :deep(.content-block__label) {
    @apply wd-mb-4 !important;
  }
  :deep(.content-block__context) {
    @apply wd-flex wd-flex-col wd-gap-8;
    .block-text {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &:last-of-type {
        margin-bottom: 0 !important;
      }

      &--right {
        text-align: right;
      }

      .sub-title {
        margin-bottom: 0;
        font-size: 13px;
        color: $gray80;
      }
    }
  }

  :deep(.content-block) {
    @include xs {
      &:last-child {
        border-bottom: none;
      }
    }
  }
}
</style>
